/*
 *  (c) 2015 - 2023 ENISCO by FORCAM GmbH
 */

import {TextField} from '@mui/material'
import React, {ChangeEvent, useState} from 'react'
import {translateText, useLanguage, useUserName} from '@enisco/mcc-react-lib'
import {FormattedMessage} from 'react-intl'
import Autocomplete from '@mui/material/Autocomplete'
import useRegisteredApps from '../../../../hooks/useRegisteredApps'
import {App, AppId} from '../../../../model/apps'
import useProfile from '../../../../hooks/useProfile'
import useFavoriteApps from '../../../../hooks/useFavoriteApps'
import appIdConverter from '../../../helper/appIdConverter'

const Apps: React.FC = () => {
  const [registeredApps] = useRegisteredApps()
  const [registeredFavorites] = useFavoriteApps()
  const language = useLanguage()
  const [profile, setProfile] = useProfile()
  const userName = useUserName()

  const allAppIds: AppId[] = []
  registeredApps.forEach((item) => {
    allAppIds.push({
      module: item.module,
      app: item.app,
      hasAccess: item.hasAccess
    })
  })
  registeredFavorites.forEach((item) => {
    allAppIds.push({
      module: item.app.module,
      app: item.app.app,
      favoriteId: item.id,
      hasAccess: item.app.hasAccess
    })
  })
  const apps: App[] = appIdConverter(
    allAppIds,
    registeredApps,
    registeredFavorites
  )

  const [selectedApp, setSelectedApp] = useState<App | null>(
    apps.find(
      (app) =>
        profile?.startupApp?.app === app.app &&
        profile?.startupApp?.module === app.module &&
        profile?.startupApp?.favoriteId === app.favoriteId
    ) || null
  )

  const onChange = (event: ChangeEvent<any>, newValue: App | null) => {
    setSelectedApp(newValue)
    const newProfile = newValue
      ? {
          startupApp: {
            module: newValue.module,
            app: newValue.app,
            favoriteId: newValue.favoriteId,
            hasAccess: newValue.hasAccess
          }
        }
      : {startupApp: undefined}

    if (userName) setProfile(newProfile)
  }

  return (
    <Autocomplete
      disabled={userName === undefined}
      sx={{width: '284px', marginTop: 2}}
      value={selectedApp as App}
      onChange={onChange}
      id='select-startup-app'
      getOptionLabel={(option) =>
        translateText(language, option.description) || ''
      }
      options={apps}
      isOptionEqualToValue={(option, value) =>
        option.description === value.description
      }
      data-testid={'Apps:autocomplete'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            <FormattedMessage
              id='MyProfileDialog.StartupApp'
              defaultMessage='Startup App'
            />
          }
          InputProps={{...params.InputProps}}
        />
      )}
    />
  )
}

export default Apps
